import { useEffect, useRef } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { PixelPlaceholderLoader, SimpleChipItem, Textfield } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { useBrandDraft } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/hooks/useBrandDraft";
import { BrandAutocompleteCombobox } from "app/pages/digital-marketing/generative-ai/shared/BrandAutocompleteCombobox/BrandAutocompleteCombobox";
import {
    INPUT_HEIGHT,
    InlineErrorMessage,
    LLMDomainAutocomplete,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMDomainAutocomplete";

import type { IRawCompetitor } from "../../types";

import { StyledCompetitorDeleteButton } from "./CompetitorsSection.styled";

const MAX_BRANDS_LINES = 2;

interface ICompetitorFieldProps {
    competitor: IRawCompetitor;
    isRemovable: boolean;
    isLoading: boolean;
    isReadOnly?: boolean;
    autoFocus?: boolean;
    domainError?: string;
    brandError?: string;
    onChange: (competitor: IRawCompetitor) => void;
    onRemove: () => void;
}

export const CompetitorField = ({
    competitor,
    isRemovable,
    isLoading,
    isReadOnly,
    autoFocus,
    domainError,
    brandError,
    onChange,
    onRemove,
}: ICompetitorFieldProps) => {
    const brandsContainerRef = useRef<HTMLDivElement>(null);
    const {
        brandFieldRef,
        brandDraft,
        setBrandDraft,
        brandDraftErrorMessage,
        canAddBrand,
        addBrand,
        commitBrand,
        onBrandKeyDown,
    } = useBrandDraft({
        existingBrands: competitor.brands,
        onAdd: (brand) => onChange({ ...competitor, brands: [...competitor.brands, brand] }),
    });

    const removeBrand = (brand: string): void => {
        onChange({
            ...competitor,
            brands: competitor.brands.filter((b) => b !== brand),
        });
    };

    useEffect(() => {
        if (brandsContainerRef.current) {
            brandsContainerRef.current.scrollTo({ top: brandsContainerRef.current.scrollHeight, behavior: "smooth" });
        }
    }, [competitor.brands]);

    const brandFieldError = brandDraftErrorMessage || brandError || "";

    return (
        <Flex
            flexDirection="column"
            gap="8px"
        >
            {isLoading ? (
                <PixelPlaceholderLoader
                    width="100%"
                    height="60px"
                />
            ) : (
                <Flex
                    alignItems="flex-start"
                    gap="12px"
                    width="100%"
                    minWidth="0"
                >
                    <Flex
                        data-automation="llm-competitor-domain-input-container"
                        flex="1 1 0"
                        minWidth="0"
                        position="relative"
                        flexDirection="column"
                    >
                        <LLMDomainAutocomplete
                            selectedDomain={competitor.domain}
                            setSelectedDomain={(domain) => (domain ? onChange({ ...competitor, domain }) : undefined)}
                            onCloseClick={isReadOnly ? undefined : () => onChange({ ...competitor, domain: null })}
                            placeholder={translate("llm.competitors_section.field.placeholder")}
                            searchIcon={!competitor.domain ? "search" : undefined}
                            autoFocus={autoFocus}
                            displaySelectedDomainIcon
                            usePortalForDropdown
                            isDisabled={isReadOnly}
                            width="100%"
                            height={INPUT_HEIGHT}
                            error={!!domainError}
                        />
                        {domainError ? (
                            <InlineErrorMessage
                                fontVariant="caption"
                                color={colorsPalettes.red[400]}
                                data-automation="llm-competitor-domain-error"
                            >
                                {domainError}
                            </InlineErrorMessage>
                        ) : null}
                    </Flex>
                    {!isReadOnly ? (
                        <Flex
                            data-automation="llm-competitor-brand-input-container"
                            flex="1 1 0"
                            minWidth="0"
                            position="relative"
                            flexDirection="column"
                        >
                            <BrandAutocompleteCombobox
                                query={brandDraft}
                                onSelect={commitBrand}
                                showTypedAsOption
                                dataAutomation="llm-competitor-brand"
                                excludedBrands={competitor.brands}
                                disabled={!!brandDraftErrorMessage}
                            >
                                <Textfield
                                    ref={brandFieldRef}
                                    data-automation="llm-competitor-brand-input"
                                    placeholder={translate("llm.setup.modal.competitor.brand.placeholder")}
                                    defaultValue={brandDraft}
                                    onChange={setBrandDraft}
                                    onKeyDown={onBrandKeyDown}
                                    error={!!brandFieldError}
                                    ApplyButtonText={translate("global.add")}
                                    onApplyClick={canAddBrand ? addBrand : undefined}
                                />
                            </BrandAutocompleteCombobox>
                            {brandFieldError ? (
                                <InlineErrorMessage
                                    fontVariant="caption"
                                    color={colorsPalettes.red[400]}
                                    data-automation="llm-competitor-brand-error"
                                >
                                    {brandFieldError}
                                </InlineErrorMessage>
                            ) : null}
                        </Flex>
                    ) : null}
                    {isRemovable ? (
                        <Flex
                            flex="0 0 32px"
                            width="32px"
                            height={INPUT_HEIGHT}
                            alignItems="center"
                            justifyContent="center"
                        >
                            <StyledCompetitorDeleteButton
                                type="flat"
                                iconName="delete"
                                iconSize="sm"
                                onClick={onRemove}
                                data-automation="llm-competitor-delete"
                            />
                        </Flex>
                    ) : null}
                </Flex>
            )}
            {!isLoading && competitor.brands.length ? (
                <Flex
                    ref={brandsContainerRef}
                    flexWrap="wrap"
                    overflow="auto"
                    maxHeight={`${40 * MAX_BRANDS_LINES}px`}
                    gap="4px"
                >
                    {competitor.brands.map((brand) => (
                        <SimpleChipItem
                            key={brand}
                            text={brand}
                            hideActionButton={isReadOnly}
                            onCloseItem={() => removeBrand(brand)}
                        />
                    ))}
                </Flex>
            ) : null}
        </Flex>
    );
};
